/**
 * reservationSyncService.ts
 *
 * 予約同期サービス
 *
 * 定期的にAPIから予約データを取得し、変更を検出してコールバックで通知する。
 * - SYNC_INTERVAL_MS ごとに予約データを取得
 * - キャッシュと比較して差分を検出（updated_at比較）
 * - 追加・更新の変更をコールバックで通知
 * - 削除検出は無効（Pusherで検出）
 */

import { fetchDispatchReservations } from '../api/dispatchService';
import {
  SYNC_INTERVAL_MS,
  SYNC_PAST_DAYS,
  SYNC_FUTURE_DAYS,
} from '../constants/calendar';
import { validateReservations } from '../schemas/dataSchemas';
import type { Reservation } from '../types';

type SyncChange = {
  type: string;
  entityType?: string;
  id?: string;
  oldData?: any;
  newData?: any;
  changedFields?: any[];
};

type SyncCallback = (changes: SyncChange[], allReservations: Reservation[]) => void;

interface SyncStatus {
  isRunning: boolean;
  cachedCount: number;
  lastSyncTime: string | null;
}

const isDev = process.env.NODE_ENV !== 'production';

class ReservationSyncService {
  intervalId: ReturnType<typeof setInterval> | null;
  cachedReservations: Map<string, any>;
  listeners: Set<SyncCallback>;
  isRunning: boolean;
  lastSyncTime: string | null;
  syncCount: number;
  isFirstSync: boolean;

  constructor() {
    this.intervalId = null;
    this.cachedReservations = new Map(); // id -> reservation
    this.listeners = new Set(); // Set<Function>
    this.isRunning = false;
    this.lastSyncTime = null;
    this.syncCount = 0;
    this.isFirstSync = true;
  }

  /**
   * リスナー登録
   */
  subscribe(callback: any): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * リスナー解除
   */
  unsubscribe(callback: any): void {
    this.listeners.delete(callback);
  }

  /**
   * 同期を開始
   */
  start(onChanges?: SyncCallback): void {
    if (onChanges) {
      this.subscribe(onChanges);
    }

    if (this.isRunning) {
      return;
    }

    this.isRunning = true;
    this.syncCount = 0;

    // 初回取得
    this.sync();

    // 定期実行
    this.intervalId = setInterval(() => {
      this.sync();
    }, SYNC_INTERVAL_MS);
  }

  /** 初回データ取得のみ（ポーリングなし） */
  startOnce(onChanges?: SyncCallback): void {
    if (onChanges) {
      this.subscribe(onChanges);
    }
    if (this.isRunning) return;
    this.isRunning = true;
    this.syncCount = 0;
    this.sync();
  }

  /** ポーリング開始（startOnce 後に呼ぶ） */
  startPolling(): void {
    if (this.intervalId) return;
    this.intervalId = setInterval(() => {
      this.sync();
    }, SYNC_INTERVAL_MS);
    console.log('[ReservationSync] Polling started');
  }

  /** ポーリング停止（キャッシュ・リスナーは維持） */
  stopPolling(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      console.log('[ReservationSync] Polling stopped');
    }
  }

  /**
   * 同期を停止
   * NOTE: キャッシュは保持する
   */
  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.isRunning = false;
  }

  /**
   * 強制同期（インターバルもリセット）
   * Promise を返すため、呼び出し元で完了を待機可能。
   */
  forceSync(): Promise<void> {
    // ポーリング状態を維持したまま即時同期
    return this.sync();
  }

  /**
   * 現在のキャッシュを取得
   */
  getReservations(): Reservation[] {
    return Array.from(this.cachedReservations.values());
  }

  /**
   * 特定の予約を取得
   */
  getReservation(id: string | number): Reservation | undefined {
    return this.cachedReservations.get(String(id));
  }

  /**
   * キャッシュにデータがあるか
   */
  hasData(): boolean {
    return this.cachedReservations.size > 0;
  }

  /**
   * 同期実行
   */
  async sync(): Promise<void> {
    try {
      this.syncCount++;
      const syncId = this.syncCount;

      const startTime = Date.now();

      // 日付範囲を計算
      const today = new Date();
      const startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - SYNC_PAST_DAYS);
      const endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + SYNC_FUTURE_DAYS);
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];

      // パラメータを強化: start_date/end_date だけでなく、貸出日/返却日の範囲も指定する
      // これにより、長期予約や特定の条件の予約が漏れるのを防ぐ
      const rawReservations = await fetchDispatchReservations({
        startDate,
        endDate,
        rentalDateFrom: startDateStr,
        rentalDateTo: endDateStr,
        returnDateFrom: startDateStr,
        returnDateTo: endDateStr
      });

      // Phase 4A: Validate and normalize all reservations at API boundary
      // Guarantees that detail is always an object (never null/undefined)
      const reservations: any[] = validateReservations(rawReservations);

      const elapsed = Date.now() - startTime;
      // 初回同期: キャッシュ初期化 + リスナーに全件通知
      if (this.isFirstSync) {
        this.updateCache(reservations);
        this.lastSyncTime = new Date().toISOString();
        this.isFirstSync = false;
        // 初回はリスナーに強制通知してContextなどがデータを受け取れるようにする
        this.notifyListeners([{ type: 'initial_load', entityType: 'reservation' }], reservations);
        return;
      }

      const changes = this.detectChanges(reservations);

      if (changes.length > 0) {
        console.log(`[ReservationSync] #${syncId} ${changes.length}件の変更を検出:`,
          changes.slice(0, 3).map(c => `${c.type}:${c.id}`).join(', ')
        );
        this.updateCache(reservations);
        this.lastSyncTime = new Date().toISOString();

        this.notifyListeners(changes, reservations);
      } else {
        this.lastSyncTime = new Date().toISOString();
      }

      const ts = new Date().toLocaleString('sv-SE').replace(' ', 'T');
      console.log(`[ReservationSync] updated(${ts})`);
    } catch (error) {
      console.error('[ReservationSync] 同期エラー:', error);
    }
  }

  /**
   * 指定期間のデータを追加読み込み（CalendarV2 の過去/日付ジャンプ用）
   * 既存キャッシュにマージする（上書きではない）
   */
  async syncRange(startDate: Date, endDate: Date): Promise<Reservation[]> {
    try {
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];
      console.log(`[ReservationSync] 範囲読み込み: ${startDateStr} 〜 ${endDateStr}`);

      const rawReservations = await fetchDispatchReservations({
        startDate,
        endDate,
        rentalDateFrom: startDateStr,
        rentalDateTo: endDateStr,
        returnDateFrom: startDateStr,
        returnDateTo: endDateStr
      });

      const reservations: any[] = validateReservations(rawReservations);
      console.log(`[ReservationSync] 範囲読み込み完了: ${reservations.length}件`);

      // 既存キャッシュにマージ（新データで上書き、既存は保持）
      for (const r of reservations) {
        this.cachedReservations.set(String(r.id), r);
      }

      // リスナーに通知
      const all = Array.from(this.cachedReservations.values());
      this.notifyListeners([{ type: 'range_load', entityType: 'reservation' }], all);

      return reservations;
    } catch (error) {
      console.error('[ReservationSync] 範囲読み込みエラー:', error);
      throw error;
    }
  }

  /**
   * 全データ同期 (Deep Search用)
   * 範囲制限を大幅に広げて取得する
   */
  async syncAll(): Promise<Reservation[]> {
    try {
      console.log('[ReservationSync] ディープ同期開始...');
      const startTime = Date.now();

      // 非常に広い範囲を指定（全データを想定）
      // バックエンド側で全件取得がサポートされている前提
      // または単に日付指定なしで呼ぶ
      const rawReservations = await fetchDispatchReservations({
        // 日付指定を外すか、極端に広い範囲を指定
        startDate: new Date(2020, 0, 1),
        endDate: new Date(2030, 0, 1)
      });

      const reservations: any[] = validateReservations(rawReservations);
      const elapsed = Date.now() - startTime;
      console.log(`[ReservationSync] ディープ同期完了: ${reservations.length}件 (${elapsed}ms)`);

      this.updateCache(reservations);
      this.lastSyncTime = new Date().toISOString();

      // 変更として通知する
      this.notifyListeners([{ type: 'sync_all', entityType: 'reservation' }], reservations);

      return reservations;
    } catch (error) {
      console.error('[ReservationSync] ディープ同期エラー:', error);
      throw error;
    }
  }

  notifyListeners(changes: SyncChange[], reservations: Reservation[]): void {
    this.listeners.forEach(callback => {
      try {
        callback(changes, reservations);
      } catch (e) {
        console.error('[ReservationSync] Listener error:', e);
      }
    });
  }

  /**
   * 差分検出
   */
  detectChanges(newReservations: Reservation[]): SyncChange[] {
    const changes: SyncChange[] = [];
    const newReservationMap = new Map(newReservations.map(r => [String(r.id), r]));

    // 更新を検出
    // NOTE: 削除検出は無効化（Pusherで検出）
    for (const [id, cached] of this.cachedReservations) {
      const newReservation = newReservationMap.get(String(id));

      if (!newReservation) {
        // APIレスポンスにない場合はスキップ（削除とみなさない）
        continue;
      } else if (this.hasChanged(cached, newReservation)) {
        changes.push({
          type: 'update',
          entityType: 'reservation',
          id,
          oldData: cached,
          newData: newReservation,
          changedFields: this.getChangedFields(cached, newReservation),
        });
      }
    }

    // 追加を検出
    for (const [id, reservation] of newReservationMap) {
      if (!this.cachedReservations.has(String(id))) {
        changes.push({
          type: 'create',
          entityType: 'reservation',
          id,
          oldData: null,
          newData: reservation,
        });
      }
    }

    return changes;
  }


  /**
   * 変更有無チェック
   */
  hasChanged(oldReservation: Reservation, newReservation: Reservation): boolean {
    const oldUpdatedAt = (oldReservation as any).updated_at || (oldReservation as any).updatedAt;
    const newUpdatedAt = (newReservation as any).updated_at || (newReservation as any).updatedAt;

    if (oldUpdatedAt && newUpdatedAt) {
      const oldTime = new Date(oldUpdatedAt).getTime();
      const newTime = new Date(newUpdatedAt).getTime();

      // 有効な日付であれば比較
      if (!isNaN(oldTime) && !isNaN(newTime)) {
        if (oldTime < newTime) return true;
        if (oldTime === newTime) return false; // 時刻が同じなら変更なしとみなす（最適化）
      }
    }

    // updated_at比較ができない、または同じ時間の場合は重要フィールドで比較
    const keyFields = [
      'status', 'status_id', 'statusId', 'vehicle_id', 'vehicleId',
      'start_date', 'end_date', 'customer_name', 'resource_id',
      'pu_staff_id', 'do_staff_id'
    ];

    for (const field of keyFields) {
      if ((oldReservation as any)[field] !== (newReservation as any)[field]) {
        return true;
      }
    }

    // JSONによる最終フォールバック（詳細データなどの変更用）
    return JSON.stringify(oldReservation) !== JSON.stringify(newReservation);
  }

  /**
   * 変更フィールド取得
   */
  getChangedFields(oldReservation: Reservation, newReservation: Reservation): any[] {
    const changed: any[] = [];

    const importantFields = [
      'status', 'status_code', 'vehicle_id', 'vehicleId',
      'start_date', 'end_date', 'customer_name', 'customer_tel',
      'resource_id', 'memo', 'remarks',
      'rental_location', 'return_location',
      'pu_staff_id', 'do_staff_id', 'rent_staff_id', 'return_staff_id',
      'mail_send_flg1', 'mail_send_flg2', 'mail_send_flg3'
    ];

    for (const key of importantFields) {
      const oldVal = (oldReservation as any)[key];
      const newVal = (newReservation as any)[key];
      if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        changed.push({ field: key, oldValue: oldVal, newValue: newVal });
      }
    }

    const detailFields = [
      'communication_tools', 'communication_tools_display',
      'pax', 'pax_children', 'child_seat', 'booster_seat', 'junior_seat',
      'arrival_flight', 'departure_flight',
      'base_price', 'option_price', 'noc_price', 'cdw_price', 'sum_price',
      'license', 'payment', 'terms_and_conditions', 'disclaimer', 'agreement',
      'comment'
    ];

    const oldDetail = (oldReservation as any).detail;
    const newDetail = (newReservation as any).detail;

    for (const key of detailFields) {
      const oldVal = (oldReservation as any)[key] !== undefined ? (oldReservation as any)[key] : oldDetail?.[key];
      const newVal = (newReservation as any)[key] !== undefined ? (newReservation as any)[key] : newDetail?.[key];
      if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        changed.push({ field: key, oldValue: oldVal, newValue: newVal });
      }
    }

    return changed;
  }

  /**
   * キャッシュ更新
   * 既存のデータより新しい場合のみ上書きする
   */
  updateCache(reservations: Reservation[]): void {
    if (!Array.isArray(reservations)) return;

    for (const newRes of reservations) {
      const id = String(newRes.id);
      const cached = this.cachedReservations.get(id);

      if (!cached) {
        // 新規
        this.cachedReservations.set(id, newRes);
      } else {
        // 既存がある場合は日付比較
        const newTime = new Date((newRes as any).updated_at || (newRes as any).updatedAt || 0).getTime();
        const cachedTime = new Date((cached as any).updated_at || (cached as any).updatedAt || 0).getTime();

        if (isNaN(newTime) || newTime >= cachedTime) {
          // APIから来たデータが新しい、または同等の場合は更新
          this.cachedReservations.set(id, newRes);
        } else {
          // APIから来たデータが古い（サーバー反映ラグ等）場合は、手動更新されたキャッシュを優先して維持
        }
      }
    }
  }

  /**
   * 単一予約のキャッシュ更新
   */
  updateSingleReservation(reservation: Reservation): void {
    if (!reservation || !reservation.id) return;
    this.cachedReservations.set(String(reservation.id), reservation);
  }

  /**
   * 初期キャッシュ設定
   */
  initializeCache(reservations: Reservation[]): void {
    if (!Array.isArray(reservations)) return;
    this.updateCache(reservations);
  }

  /**
   * 状態取得
   */
  getStatus(): SyncStatus {
    return {
      isRunning: this.isRunning,
      cachedCount: this.cachedReservations.size,
      lastSyncTime: this.lastSyncTime,
    };
  }
}

export const reservationSyncService = new ReservationSyncService();
